type PrepareStackTraceFn = NonNullable<(typeof Error)["prepareStackTrace"]>;
export type StructuredStackTrace = Parameters<PrepareStackTraceFn>[1];

// getThis: returns the value of this
// getTypeName: returns the type of this as a string. This is the name of the function stored in the constructor field of this, if available, otherwise the object’s [[Class]] internal property.
// getFunction: returns the current function
// getFunctionName: returns the name of the current function, typically its name property. If a name property is not available an attempt is made to infer a name from the function’s context.
// getMethodName: returns the name of the property of this or one of its prototypes that holds the current function
// getFileName: if this function was defined in a script returns the name of the script
// getLineNumber: if this function was defined in a script returns the current line number
// getColumnNumber: if this function was defined in a script returns the current column number
// getEvalOrigin: if this function was created using a call to eval returns a string representing the location where eval was called
// isToplevel: is this a top-level invocation, that is, is this the global object?
// isEval: does this call take place in code defined by a call to eval?
// isNative: is this call in native V8 code?
// isConstructor: is this a constructor call?
// isAsync: is this an async call (i.e. await, Promise.all(), or Promise.any())?
// isPromiseAll: is this an async call to Promise.all()?
// getPromiseIndex: returns the index of the promise element that was followed in Promise.all() or Promise.any() for async stack traces, or null if the CallSite is not an async Promise.all() or Promise.any() call.
export const serializeStructuredStackTrace = (structuredStackTrace: StructuredStackTrace) => structuredStackTrace.map(callSite => {
	const isToplevel = callSite.isToplevel();
	const isEval = callSite.isEval();
	const isNative = callSite.isNative();
	const isConstructor = callSite.isConstructor();
	const isAsync = callSite.isAsync();
	const isPromiseAll = callSite.isPromiseAll();
	const columnNumber = callSite.getColumnNumber();
	const lineNumber = callSite.getLineNumber();
	const fileName = callSite.getFileName();
	const functionName = callSite.getFunctionName();
	const methodName = callSite.getMethodName();
	const typeName = callSite.getTypeName();
	return {
		...(isToplevel ? { isToplevel: true } as const : {}),
		...(isEval ? { isEval: true, evalOrigin: callSite.getEvalOrigin() } as const : {}),
		...(isNative ? { isNative: true } as const : {}),
		...(isConstructor ? { isConstructor: true } as const : {}),
		...(isAsync ? { isAsync: true } as const : {}),
		...(isPromiseAll ? { isPromiseAll: true } as const : {}),
		...(columnNumber != null ? { columnNumber } as const : {}),
		...(lineNumber != null ? { lineNumber } as const : {}),
		...(fileName != null ? { fileName } as const : {}),
		...(functionName != null ? { functionName } as const : {}),
		...(methodName != null ? { methodName } as const : {}),
		...(typeof typeName === "string" ? { typeName } as const : {}),
	} as const;
});
export type StackArray = ReturnType<typeof serializeStructuredStackTrace>;
export type StackCallSite = NonNullable<StackArray[number]>;
